/**
 * Consent-aware order attribution.
 *
 * Stores a 30-day first touch and a per-session touch, then writes only
 * sanitised source metadata to Shopify cart attributes after a successful cart
 * mutation. No query strings, full URLs, referrer paths, or user-agent data are
 * retained.
 *
 * CONSENT CATEGORY — analytics, not marketing (changed 2026-09-03).
 * ────────────────────────────────────────────────────────────────
 * This file previously gated on `marketingAllowed()`. That is the wrong category
 * for what it does. The stamp it writes ("Traffic source", "Landing page",
 * "Session source") lands on the merchant's OWN order record and is read only by
 * internal reporting — AIM's order page and the growth dashboard's channel
 * breakdown. It is not shared with an ad network, not used to build an audience,
 * and not used to retarget. Under UK GDPR/PECR that is a statistics/measurement
 * purpose, i.e. the `analytics` category; `marketing` covers advertising and
 * retargeting. Shopify's Customer Privacy API draws the same line:
 * `analyticsProcessingAllowed()` for measuring site usage, `marketingAllowed()`
 * for advertising.
 *
 * Measured cost of the old gate: of the 50 most recent orders (18 Aug – 2 Sep
 * 2026), 4 (8%) had a full Shopify customer journey — so the visitor HAD granted
 * analytics consent — yet carried no stamp at all, because they declined
 * marketing. Order #KT-264-2251 is the worked example: 3 sessions over 4 days
 * from Google CPC in Shopify's own Conversion summary, beside an empty
 * "Additional details" card.
 *
 * This does NOT widen what is collected, only which of the visitor's two
 * existing choices unlocks it. A visitor who declines analytics still gets
 * nothing read, stored or sent — that is the 38% bucket, and it is untouched.
 */
(function () {
  'use strict';

  var FIRST_KEY = 'kt-attribution-first';
  var SESSION_KEY = 'kt-attribution-session';
  var WINDOW_MS = 30 * 24 * 60 * 60 * 1000;
  var syncing = false;
  var syncQueued = false;
  var consentRevision = 0;
  var nativeFetch = window.fetch.bind(window);

  // Named `attributionAllowed` rather than `analyticsAllowed` so the call sites
  // read as "may I attribute this order?" — the question this file asks — and so
  // a future reader does not assume it is a generic analytics helper. See the
  // CONSENT CATEGORY note in the file header for why this is analytics.
  function attributionAllowed() {
    if (window.KTConsent && typeof window.KTConsent.analytics === 'function') {
      return window.KTConsent.analytics() === true;
    }
    try {
      return !!(window.Shopify && window.Shopify.customerPrivacy &&
        typeof window.Shopify.customerPrivacy.analyticsProcessingAllowed === 'function' &&
        window.Shopify.customerPrivacy.analyticsProcessingAllowed());
    } catch (_) { return false; }
  }

  // The Customer Privacy API only initialises after privacyBanner.loadBanner()
  // (which the theme arms on first interaction — see
  // snippets/shopify-consent-banner-loader.liquid). If a cart mutation lands
  // before that, ensure the OFFICIAL consent-tracking API is loaded so consent
  // state can be READ (loading it never grants consent; opt-in regions stay
  // denied until the visitor accepts the banner).
  // KT-192: window.KTConsent (snippets/kt-consent.liquid) owns loading the API now, so
  // this only has to wait for it. The old guard tested `window.Shopify.customerPrivacy`
  // for TRUTHINESS, but privacyBanner installs a method-less stub
  // (`{ cachedConsent, consentStatus }`) on the visit where the banner renders — the
  // guard passed on the stub, loadFeatures was skipped, and marketingAllowed() then
  // threw. Probe for the METHOD, never the object.
  var privacyApiRequested = false;
  function privacyApiReady() {
    try {
      var cp = window.Shopify && window.Shopify.customerPrivacy;
      return !!(cp && typeof cp.analyticsProcessingAllowed === 'function');
    } catch (_) { return false; }
  }
  function ensurePrivacyApi(done) {
    if (window.KTConsent && typeof window.KTConsent.load === 'function') {
      if (window.KTConsent.state().known || privacyApiReady()) return done();
      window.KTConsent.load();
      // KTConsent calls back through onChange when the API resolves; syncToCart is
      // re-queued from there, so nothing is lost by returning now.
      return done();
    }
    if (privacyApiReady()) return done();
    if (!privacyApiRequested && window.Shopify && typeof window.Shopify.loadFeatures === 'function') {
      privacyApiRequested = true;
      try {
        window.Shopify.loadFeatures(
          [{ name: 'consent-tracking-api', version: '0.1' }],
          function () { done(); }
        );
        return;
      } catch (_) { /* fall through */ }
    }
    done();
  }

  function clean(value, limit) {
    return String(value || '').replace(/[^\w .|/+:-]/g, '').trim().slice(0, limit || 120);
  }

  function referrerHost() {
    try { return new URL(document.referrer).hostname.replace(/^www\./, '').toLowerCase(); }
    catch (_) { return ''; }
  }

  // KT-194: classification is delegated to window.KTChannels
  // (snippets/kt-channel-taxonomy.liquid, generated from config/utm-taxonomy.json —
  // the same lists AIMarketOps and the feed/video scripts use). The three regexes
  // this replaced knew six social networks and three click identifiers, so Pinterest
  // and YouTube were the only ones of the store's own publishing channels they could
  // name, TikTok/Reddit/Threads/Bluesky/WhatsApp arrivals all landed in "referral",
  // and every paid click that strips its referrer (ttclid, li_fat_id, epik, twclid,
  // sccid, rdt_cid, wbraid/gbraid) was recorded as "direct".
  //
  // The `<source> / <channel>` shape is unchanged — it is what already appears on
  // orders as the `Traffic source` attribute.
  var CLICK_ID_KEYS = ['gclid', 'wbraid', 'gbraid', 'dclid', 'gclsrc', 'msclkid',
    'fbclid', 'igshid', 'ttclid', 'li_fat_id', 'epik', 'twclid', 'sccid', 'rdt_cid',
    'irclickid', 'awc', 'clickref', 'cjevent'];

  function classify(params, host) {
    if (window.KTChannels && typeof window.KTChannels.classify === 'function') {
      var r = window.KTChannels.classify(host, params);
      if (!r || r.channel === 'internal') return '';
      return clean(r.source) + ' / ' + clean(r.channel);
    }
    // KTChannels absent (snippet not rendered) — the previous behaviour, so
    // attribution degrades to coarse rather than disappearing.
    if (params.utm_source) return clean(params.utm_source) + (params.utm_medium ? ' / ' + clean(params.utm_medium) : '');
    if (params.gclid) return 'google / cpc';
    if (params.msclkid) return 'bing / cpc';
    if (params.fbclid) return 'facebook / paid-social';
    if (!host) return 'direct';
    if (host === location.hostname.replace(/^www\./, '').toLowerCase()) return '';
    return host + ' / referral';
  }

  function currentTouch() {
    var params = {};
    try {
      var query = new URLSearchParams(location.search);
      ['utm_source', 'utm_medium', 'utm_campaign'].concat(CLICK_ID_KEYS).forEach(function (key) {
        if (query.get(key)) params[key] = clean(query.get(key));
      });
    } catch (_) {}
    var host = referrerHost();
    var source = classify(params, host);
    if (!source) return null;
    return {
      ts: Date.now(),
      source: source,
      campaign: clean(params.utm_campaign),
      landing: clean(location.pathname, 180),
      referrer: clean(host)
    };
  }

  function read(storage, key) {
    try { return JSON.parse(storage.getItem(key) || 'null'); } catch (_) { return null; }
  }

  function clearStoredTouches() {
    try { localStorage.removeItem(FIRST_KEY); } catch (_) {}
    try { sessionStorage.removeItem(SESSION_KEY); } catch (_) {}
  }

  function resolveTouches() {
    if (!attributionAllowed()) return null;
    var now = Date.now();
    var current = currentTouch();
    var first = read(localStorage, FIRST_KEY);
    if (!first || !first.ts || now - first.ts >= WINDOW_MS) {
      first = current || { ts: now, source: 'direct', campaign: '', landing: clean(location.pathname, 180), referrer: '' };
      try { localStorage.setItem(FIRST_KEY, JSON.stringify(first)); } catch (_) {}
    }
    var session = read(sessionStorage, SESSION_KEY);
    if (!session) {
      session = current || first;
      try { sessionStorage.setItem(SESSION_KEY, JSON.stringify(session)); } catch (_) {}
    }
    return { first: first, session: session };
  }

  function syncToCart() {
    if (syncing || !attributionAllowed()) return;
    var touches = resolveTouches();
    if (!touches) return;
    var revision = consentRevision;
    syncing = true;
    nativeFetch('/cart.js', { headers: { Accept: 'application/json' } })
      .then(function (response) { return response.ok ? response.json() : null; })
      .then(function (cart) {
        // Consent can change while either the request or JSON body is pending.
        // A later opt-in must not revive data captured before withdrawal.
        if (!attributionAllowed() || revision !== consentRevision) return null;
        if (!cart || cart.item_count === 0) return null;
        var existing = cart.attributes || {};
        var attrs = {
          'Traffic source': touches.first.source,
          'Landing page': touches.first.landing,
          'First seen': new Date(touches.first.ts).toISOString().slice(0, 10),
          'Session source': touches.session.source
        };
        if (touches.first.campaign) attrs.Campaign = touches.first.campaign;
        if (touches.first.referrer) attrs.Referrer = touches.first.referrer;
        var changed = {};
        Object.keys(attrs).forEach(function (key) {
          if (attrs[key] && existing[key] !== attrs[key]) changed[key] = attrs[key];
        });
        if (!Object.keys(changed).length) return null;
        // Resource timing cannot inspect X-KT-Attribution. A constant URL marker
        // lets our observer ignore this write even when it fails and leaves the
        // cart unchanged. No visitor data is added to the URL.
        return nativeFetch('/cart/update.js?kt_attribution=1', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'X-KT-Attribution': '1' },
          body: JSON.stringify({ attributes: changed })
        });
      })
      .catch(function () {})
      .finally(function () {
        syncing = false;
        if (revision !== consentRevision && attributionAllowed()) queueSync();
      });
  }

  function queueSync() {
    if (syncQueued) return;
    syncQueued = true;
    setTimeout(function () {
      syncQueued = false;
      ensurePrivacyApi(syncToCart);
    }, 0);
  }

  // KT-229: observe cart mutations WITHOUT standing in the call stack.
  //
  // This used to wrap `window.fetch` purely to notice cart writes — it never modified a
  // request. The cost of that was misattribution: because the wrapper called
  // `nativeFetch.apply(...)`, it became the last JS frame before EVERY cart request, so
  // Chrome blamed `kt-attribution.js` for failures raised by the theme's own stepper. Two
  // separate investigations were sent to this file over a `POST /cart/change.js 400` whose
  // real caller was `interactable.min.js`. A wrapper that only listens should not be able to
  // do that.
  //
  // Resource timing sees the same requests and is not in the stack. The URL pattern already
  // separates mutations from reads — nothing GETs /cart/change.js, and /cart.js does not
  // match — so losing the method check costs nothing.
  //
  // Ignore our own writes explicitly. A failed write leaves the old attributes
  // intact, so merely comparing values would repeatedly retry its timing entry.
  // Other cart mutations and existing DOM events still trigger a fresh sync.
  if (typeof PerformanceObserver === 'function') {
    try {
      var CART_MUTATION = /\/cart\/(add|change|update|clear)(\.js)?(?:\?|$)/;
      new PerformanceObserver(function (list) {
        var entries = list.getEntries();
        for (var i = 0; i < entries.length; i++) {
          if (CART_MUTATION.test(entries[i].name) && !/[?&]kt_attribution=1(?:&|$)/.test(entries[i].name)) {
            queueSync();
            return;
          }
        }
      }).observe({ type: 'resource', buffered: true });
    } catch (error) {
      // Resource timing unavailable. The cart:updated / cart:refresh listeners below still
      // cover the theme's own events; attribution degrades, the storefront does not.
    }
  }

  document.addEventListener('cart:updated', queueSync);
  document.addEventListener('cart:refresh', queueSync);
  // `visitorConsentCollected` never reaches a returning visitor who already decided,
  // so subscribe to KTConsent as well — it reports the resolved state on load.
  function onConsent() {
    if (attributionAllowed()) queueSync();
    else {
      consentRevision += 1;
      clearStoredTouches();
    }
  }
  document.addEventListener('visitorConsentCollected', onConsent);
  if (window.KTConsent && typeof window.KTConsent.onChange === 'function') {
    window.KTConsent.onChange(function (state) { if (state.known) onConsent(); });
  }
  window.addEventListener('pageshow', function (event) {
    if (event.persisted) queueSync();
  });
  if (window.jQuery_T4NT) window.jQuery_T4NT(document).on('cart:updated cart:refresh', queueSync);

  if (attributionAllowed()) queueSync();
})();
